import React, { useEffect, useState } from 'react';
import { Box, Typography, Chip, Grid, LinearProgress } from '@mui/material';
import { Trophy, Users, DollarSign, MapPin, Megaphone } from 'lucide-react';
import { environment } from '../config/environment';
import { getApiHeadersForGet } from '../utils/apiUtils';

type MediaKind = 'image' | 'video' | 'audio' | 'document' | 'other';

interface ProposalMedia {
  kind: MediaKind;
  url: string;
  name?: string;
}

interface ProposalActivation {
  itemKey?: string;
  categoryKey?: string;
  title?: string;
  description?: string;
  category?: string;
  quantity?: number;
  priceUsd?: number;
  reachEstK?: number;
  media?: ProposalMedia[];
}

interface ProposalTerritory {
  region?: string;
  radiusMiles?: number;
  petParents?: number;
  shelters?: number;
  activationCount?: number;
}

interface ProposalContent {
  proposalNumber?: string;
  status?: string;
  quarter?: string;
  hero?: {
    headline?: string;
    subcopy?: string;
    missionParagraphs?: string[];
    nextSteps?: string[];
  };
  coverPhotoUrl?: string;
  metrics?: {
    totalImpactScore?: number;
    totalReachEstK?: number;
    totalInvestmentUsd?: number;
    totalDeployments?: number;
    totalTerritories?: number;
    totalPetParentsEstimate?: number;
    totalSheltersEstimate?: number;
  };
  sponsor?: { displayName?: string; logoUrl?: string };
  territories?: ProposalTerritory[];
  activations?: ProposalActivation[];
  prize?: {
    totalUsd?: number;
    secondaryUsd?: number;
    poolUsd?: number;
    currency?: string;
    type?: string;
    places?: { place: number; percent: number; amountUsd: number }[];
  } | null;
}

interface ProposalItem {
  id: string;
  contest_id: string;
  proposal_id?: string;
  proposal_number?: string;
  status?: string;
  content: ProposalContent;
  updated_at?: string;
}

interface ProposalShowcaseProps {
  contestId: string;
  // Permite inyectar un item ya cargado (evita un fetch). Si no se pasa, el
  // componente consulta el endpoint por su cuenta.
  item?: ProposalItem | null;
  // Cuando es true, no muestra el contenedor con borde (uso embebido en dialog).
  embedded?: boolean;
  // Título de sección opcional; solo se muestra cuando hay propuesta.
  title?: string;
}

const fmtMoney = (value?: number) =>
  `$${Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

const fmtNum = (value?: number) => Number(value || 0).toLocaleString('en-US');

const MediaTile: React.FC<{ media: ProposalMedia }> = ({ media }) => {
  if (media.kind === 'video') {
    return (
      <Box
        component="video"
        src={media.url}
        controls
        sx={{ width: '100%', borderRadius: 1.5, backgroundColor: '#000', maxHeight: 320 }}
      />
    );
  }
  if (media.kind === 'audio') {
    return <Box component="audio" src={media.url} controls sx={{ width: '100%' }} />;
  }
  if (media.kind === 'document') {
    return (
      <Box
        component="a"
        href={media.url}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}
      >
        {media.name || 'Ver documento'}
      </Box>
    );
  }
  return (
    <Box
      component="img"
      src={media.url}
      alt={media.name || 'media'}
      sx={{ width: '100%', borderRadius: 1.5, objectFit: 'cover', maxHeight: 320 }}
    />
  );
};

const MetricCard: React.FC<{ icon: React.ReactNode; label: string; value: string }> = ({ icon, label, value }) => (
  <Box sx={{ p: 1.75, borderRadius: 2, backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', height: '100%' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, color: '#6b7280' }}>
      {icon}
      <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </Typography>
    </Box>
    <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827' }}>
      {value}
    </Typography>
  </Box>
);

const ProposalShowcase: React.FC<ProposalShowcaseProps> = ({ contestId, item: itemProp, embedded = false, title }) => {
  const [item, setItem] = useState<ProposalItem | null>(itemProp || null);
  const [loading, setLoading] = useState(!itemProp);
  const [error, setError] = useState('');

  useEffect(() => {
    if (itemProp) {
      setItem(itemProp);
      setLoading(false);
      return;
    }
    if (!contestId) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(
          `${environment.apiBaseUrl}/contest-proposal-content/contest/${encodeURIComponent(contestId)}`,
          { headers: getApiHeadersForGet() }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const items: ProposalItem[] = data?.items || [];
        if (!cancelled) setItem(items[0] || null);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Error cargando la propuesta');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [contestId, itemProp]);

  if (loading) {
    return (
      <Box sx={{ py: 2 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (error || !item) {
    // Sin propuesta enlazada: no renderizamos nada para no ensuciar la vista.
    return null;
  }

  const content = item.content || {};
  const hero = content.hero || {};
  const metrics = content.metrics || {};
  const territories = content.territories || [];
  // SB-004: el ítem de premio (c4 "Winner Reward") se muestra en el bloque de
  // PREMIO, no entre las activaciones del paquete. Se filtra por robustez aunque
  // el serializer ya lo excluye (cubre contenido sincronizado antes del fix).
  const activations = (content.activations || []).filter((a) => a?.itemKey !== 'c4');
  const prize = content.prize || null;
  const prizePlaces = prize?.places || [];
  const placeLabels = ['1er lugar', '2do lugar', '3er lugar', '4to lugar'];

  return (
    <Box
      sx={
        embedded
          ? {}
          : { p: { xs: 2, sm: 3 }, borderRadius: 3, border: '1px solid #e5e7eb', backgroundColor: '#fff' }
      }
    >
      {title && (
        <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827', mb: 2 }}>
          {title}
        </Typography>
      )}

      {/* Encabezado del sponsor */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        {content.sponsor?.logoUrl && (
          <Box
            component="img"
            src={content.sponsor.logoUrl}
            alt={content.sponsor?.displayName || 'Sponsor'}
            sx={{ width: 44, height: 44, objectFit: 'contain', borderRadius: 1, border: '1px solid #e5e7eb', backgroundColor: '#fff', p: 0.5 }}
          />
        )}
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#111827' }}>
            {content.sponsor?.displayName || 'Propuesta de patrocinio'}
          </Typography>
          {item.proposal_number && (
            <Typography variant="caption" sx={{ color: '#6b7280' }}>
              {item.proposal_number}
              {content.quarter ? ` · ${content.quarter}` : ''}
            </Typography>
          )}
        </Box>
        {item.status && <Chip size="small" label={item.status} color="secondary" variant="outlined" />}
      </Box>

      {/* Hero */}
      {(hero.headline || hero.subcopy) && (
        <Box sx={{ mb: 2.5 }}>
          {hero.headline && (
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#111827', mb: 0.5 }}>
              {hero.headline}
            </Typography>
          )}
          {hero.subcopy && (
            <Typography variant="body2" sx={{ color: '#4b5563' }}>
              {hero.subcopy}
            </Typography>
          )}
        </Box>
      )}

      {/* Métricas */}
      <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <MetricCard icon={<Trophy className="w-4 h-4" />} label="Impacto" value={fmtNum(metrics.totalImpactScore)} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <MetricCard icon={<Users className="w-4 h-4" />} label="Alcance" value={`${fmtNum(metrics.totalReachEstK)}k`} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <MetricCard icon={<Megaphone className="w-4 h-4" />} label="Activaciones" value={fmtNum(metrics.totalDeployments)} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <MetricCard icon={<DollarSign className="w-4 h-4" />} label="Inversión" value={fmtMoney(metrics.totalInvestmentUsd)} />
        </Grid>
      </Grid>

      {/* Premio del concurso (armado en la propuesta) */}
      {prize && Number(prize.totalUsd || 0) > 0 && (
        <Box sx={{ mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Trophy className="w-4 h-4" color="#ED1790" />
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#111827' }}>
              Premio del concurso
            </Typography>
            <Chip
              size="small"
              label={`Pozo total ${fmtMoney(prize.poolUsd || (Number(prize.totalUsd || 0) + Number(prize.secondaryUsd || 0)))}`}
              sx={{ ml: 'auto', fontWeight: 700, backgroundColor: '#FCE7F3', color: '#9D174D' }}
            />
          </Box>
          {/* SB-003/SB-001: sponsor que financia el premio y su aporte. */}
          {content.sponsor?.displayName && (
            <Typography variant="caption" sx={{ display: 'block', color: '#6b7280', mb: 1 }}>
              Financiado por <strong style={{ color: '#111827' }}>{content.sponsor.displayName}</strong>
              {' · '}Aporte del patrocinador <strong style={{ color: '#047857' }}>{fmtMoney(prize.poolUsd || (Number(prize.totalUsd || 0) + Number(prize.secondaryUsd || 0)))}</strong>
            </Typography>
          )}
          <Grid container spacing={1.5}>
            {(prizePlaces.length > 0 ? prizePlaces : []).map((p, idx) => (
              <Grid size={{ xs: 6, md: 3 }} key={p.place ?? idx}>
                <Box
                  sx={{
                    p: 1.75,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: idx === 0 ? '#FBCFE8' : '#e5e7eb',
                    backgroundColor: idx === 0 ? '#FDF2F8' : '#f9fafb',
                    height: '100%'
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {placeLabels[idx] || `${p.place}º`} · {p.percent}%
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: idx === 0 ? '#9D174D' : '#111827' }}>
                    {fmtMoney(p.amountUsd)}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          {Number(prize.secondaryUsd || 0) > 0 && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1.25, color: '#6b7280' }}>
              Premio secundario (demás participantes): <strong style={{ color: '#111827' }}>{fmtMoney(prize.secondaryUsd)}</strong>
            </Typography>
          )}
        </Box>
      )}

      {/* Territorios */}
      {territories.length > 0 && (
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#111827', mb: 1 }}>
            Territorios
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {territories.map((t, idx) => (
              <Chip
                key={idx}
                icon={<MapPin className="w-3.5 h-3.5" />}
                label={`${t.region || 'Territorio'}${t.radiusMiles ? ` · ${t.radiusMiles}mi` : ''}${t.activationCount ? ` · ${t.activationCount} act.` : ''}`}
                variant="outlined"
                size="small"
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Activaciones / paquete */}
      {activations.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#111827', mb: 1.25 }}>
            Paquete de activaciones
          </Typography>
          <Grid container spacing={2}>
            {activations.map((act, idx) => (
              <Grid size={{ xs: 12, sm: 6 }} key={idx}>
                <Box sx={{ p: 1.75, borderRadius: 2, border: '1px solid #e5e7eb', height: '100%' }}>
                  {act.category && (
                    <Typography variant="caption" sx={{ color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      {act.category}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#111827' }}>
                      {act.title || 'Activación'}
                      {act.quantity && act.quantity > 1 ? ` ×${act.quantity}` : ''}
                    </Typography>
                    {act.priceUsd ? (
                      <Typography variant="body2" sx={{ fontWeight: 800, color: '#ED1790' }}>
                        {fmtMoney(act.priceUsd)}
                      </Typography>
                    ) : null}
                  </Box>
                  {act.description && (
                    <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                      {act.description}
                    </Typography>
                  )}
                  {Array.isArray(act.media) && act.media.length > 0 && (
                    <Box sx={{ display: 'grid', gap: 1 }}>
                      {act.media.map((m, mIdx) => (
                        <MediaTile key={mIdx} media={m} />
                      ))}
                    </Box>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default ProposalShowcase;
